// Angular
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
// npm
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
// local lib
import { IFilterControl } from '../../../types/internal-interfaces';
import { ITypeaheadFilterControl } from '../../../shared/types/interfaces';
import { MultiFilterTypeaheadConfig } from './multi-filter-typeahead-config';
import { MultiFilterTypeaheadLoadComplete } from './multi-filter-typeahead-load-complete';

// lib
import { AuthenticationService } from '@libs/authentication/services';

@UntilDestroy()
@Component({
  selector: 'multi-filter-typeahead',
  templateUrl: 'multi-filter-typeahead.component.html',
  styleUrls: ['./multi-filter-typeahead.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiFilterTypeaheadComponent
  implements OnInit, OnChanges, IFilterControl
{
  private readonly noLocationsMessage =
    'No Locations found, please refresh the page';

  @Input() configuration: MultiFilterTypeaheadConfig;

  @Output() loadComplete: EventEmitter<MultiFilterTypeaheadLoadComplete> =
    new EventEmitter();
  @Output() errorOccurred: EventEmitter<string> = new EventEmitter();
  @Output() filterUpdated: EventEmitter<string[]> = new EventEmitter();
  @Output() optionsSelectedCountUpdated: EventEmitter<number> =
    new EventEmitter();

  public cachedSelectedOptions: string[] = [];
  public visibleOptions: ITypeaheadFilterControl[] = [];

  public needsSearch = false;
  public labelText = '';
  public searchString: string;

  public selectedOptionsCount: number = 0;
  get shouldDisplaySelectedOptionsCount(): boolean {
    return this.selectedOptionsCount > 0;
  }
  shouldDisplayNoResults: boolean = false;

  private searchSubject = new Subject<string>();
  private throttledSearch$: Observable<string> =
    this.searchSubject.asObservable();
  private delay = 100;

  constructor(
    private auth: AuthenticationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    const hasChanges = this.hasChanges(
      changes.configuration.currentValue,
      changes.configuration.previousValue,
    );
    if (hasChanges) {
      this.initializeFilter();
    }
  }

  public setValue(currentOptions: string[]): void {
    currentOptions = currentOptions ?? [];
    this.clearSelections();

    if (this.needsSearch) {
      this.cachedSelectedOptions = [...currentOptions];
    }

    this.visibleOptions.forEach((option: ITypeaheadFilterControl) => {
      option.isSelected = currentOptions.indexOf(option.value) > -1;
    });

    this.selectedOptionsCount = currentOptions.length;
    this.optionsSelectedCountUpdated.emit(currentOptions.length);
    this.cdr.detectChanges();
  }

  public searchOptions(): void {
    if (this.needsSearch) {
      // Send up search string for API to search with and await new filterOptions
      this.searchSubject.next(this.searchString);
    } else {
      // Search from the original filterOptions passed in
      this.visibleOptions.forEach((option: ITypeaheadFilterControl) => {
        const searchStringToLower = this.searchString.toLowerCase();
        const leftSearch = option.bottomTextLeft ?? '';
        const rightSearch = option.bottomTextRight ?? '';

        if (
          option.label.toLowerCase().indexOf(searchStringToLower) !== -1 ||
          leftSearch.toLowerCase().indexOf(searchStringToLower) !== -1 ||
          rightSearch.toLowerCase().indexOf(searchStringToLower) !== -1
        ) {
          option.isHidden = false;
        } else {
          option.isHidden = true;
        }
      });

      this.shouldDisplayNoResults =
        this.searchString?.length > 1 &&
        this.visibleOptions.filter((o: ITypeaheadFilterControl) => !o.isHidden)
          .length === 0;
    }
  }

  public clearAllOptions(): void {
    // clear options cache
    if (this.needsSearch) this.cachedSelectedOptions = [];
    // Unselect visible options
    this.visibleOptions.forEach((option: ITypeaheadFilterControl) => {
      option.isSelected = false;
    });
    // Update the count in UI
    this.updateCount(true, true);
    // Emit signal to parents
    this.sendOptionsPayload();
  }

  public handleCheckedFilterOption(option: ITypeaheadFilterControl): void {
    // Cache seleced option, and remove ones unselected.
    if (this.needsSearch) this.updateCheckedOptionsCache(option);

    this.updateCount(option.isSelected);
    this.sendOptionsPayload();
  }

  private hasChanges(
    current: MultiFilterTypeaheadConfig,
    previous: MultiFilterTypeaheadConfig,
  ): boolean {
    const changed =
      current.accountId != previous?.accountId ||
      current.labelText != previous?.labelText;
    return changed;
  }

  // Private/Component.ts only methods
  private sendOptionsPayload(): void {
    let payload: string[];

    // Set payload from cached selected filters (api search being used),
    if (this.needsSearch) {
      payload = [...this.cachedSelectedOptions];
    } else {
      // or from the original set of filters (api search not needed)
      payload = [];
      this.visibleOptions.forEach((option: ITypeaheadFilterControl) => {
        if (option.isSelected) payload.push(option.value);
      });
    }

    this.filterUpdated.emit(payload);
  }

  private initFilterSearchListener(): void {
    this.throttledSearch$
      .pipe(
        debounceTime(this.delay),
        untilDestroyed(this),
        switchMap((searchString: string) => {
          // only search if 2 or more chars are used to stop excessive searches
          if (searchString?.length >= 2) {
            return this.externalSearch(searchString);
          } else {
            this.visibleOptions = []; // reset when search is cleared
            this.cdr.detectChanges();
            return this.visibleOptions;
          }
        }),
        catchError(() => {
          this.errorOccurred.emit(this.noLocationsMessage);
          return of([]);
        }),
      )
      .subscribe();
  }

  private setFilterOptionsFromConfig(filters: ITypeaheadFilterControl[]): void {
    this.visibleOptions = [];

    // set visible from newest filterOptions
    filters.forEach((option: ITypeaheadFilterControl) => {
      if (option.isSelected) {
        if (this.needsSearch) this.cachedSelectedOptions.push(option.value);

        this.updateCount(true);
      }
      this.visibleOptions.push(Object.assign({}, option));
    });

    // If search is being used, check cache for prev selected options
    if (this.needsSearch) {
      this.cachedSelectedOptions.forEach((option: string) => {
        const prevSelectedOption: ITypeaheadFilterControl =
          this.visibleOptions.find(
            (visibleOption: ITypeaheadFilterControl) =>
              visibleOption.value === option,
          );

        if (prevSelectedOption) prevSelectedOption.isSelected = true;
      });
    }

    this.shouldDisplayNoResults =
      this.searchString?.length > 1 && this.visibleOptions?.length === 0;
  }

  private updateCount(add: boolean, reset: boolean = false): void {
    if (reset) {
      this.selectedOptionsCount = 0;
    } else {
      add ? this.selectedOptionsCount++ : this.selectedOptionsCount--;
    }

    this.optionsSelectedCountUpdated.emit(this.selectedOptionsCount);
  }

  private updateCheckedOptionsCache(option: ITypeaheadFilterControl): void {
    if (option.isSelected) {
      this.cachedSelectedOptions.push(option.value);
    } else {
      this.cachedSelectedOptions = this.cachedSelectedOptions.filter(
        (cachedOption: string) => option.value !== cachedOption,
      );
    }
  }

  private clearSelections(): void {
    this.cachedSelectedOptions = [];
    this.visibleOptions.forEach(
      (option: ITypeaheadFilterControl) => (option.isSelected = false),
    );
  }

  private externalSearch(searchString: string): Observable<any> {
    return this.configuration.typeaheadService
      .search(searchString, this.auth.account$.getValue().id)
      .pipe(
        map((filters: ITypeaheadFilterControl[]) => {
          this.setFilterOptionsFromConfig(filters);
          this.cdr.detectChanges();
        }),
      );
  }

  private initializeFilter(): void {
    this.configuration.typeaheadService
      .getFirstPage(this.configuration.accountId)
      .subscribe(
        (filters: ITypeaheadFilterControl[]) => {
          this.needsSearch = filters.length > 50;
          this.labelText = this.configuration.labelText;

          if (this.needsSearch) {
            this.initFilterSearchListener();
          } else {
            this.setFilterOptionsFromConfig(filters);
          }

          this.loadComplete.emit({
            hasOneOption: filters.length == 1,
          });
          this.cdr.detectChanges();
        },
        () => {
          this.errorOccurred.emit(this.noLocationsMessage);
        },
      );
  }
}
